'use client'
import { useEffect } from 'react'
import ErrorBoundary from '@error-boundary'
import dynamic from 'next/dynamic'

import type { AcfForm } from '@typings'
import {
  RaButton,
  RaHeading,
  RaIcon,
  RaRichText,
  RaTypography,
} from '@components/core/index.core'
import { ICONS } from '@constants/icons.constants'
import { useForm } from '@contexts/form.context'
import { useSettings } from '@contexts/settings.context'
import { useUi } from '@contexts/ui.context'
import { translate } from '@utilities/helpers/core.helpers'
import { useViewport } from '@utilities/hooks/useViewport.hooks'

import { RaFormSkeletonLoader } from './RaFormSkeletonLoader'

const GravityFormsPage = dynamic(() =>
  import('@components/gravity-forms/GravityFormsPage/GravityFormsPage').then(
    ({ GravityFormsPage }) => ({
      default: GravityFormsPage,
    })
  )
)

const RaFormPagination = dynamic(() =>
  import('@components/form-elements/RaFormPagination/RaFormPagination').then(
    ({ RaFormPagination }) => {
      return {
        default: RaFormPagination,
      }
    }
  )
)

const RaFormStyles = dynamic(() =>
  import('@components/form-elements/RaFormStyles/RaFormStyles').then(
    ({ RaFormStyles }) => {
      return {
        default: RaFormStyles,
      }
    }
  )
)

export const RaForm = ({ expires, history, id, redirect }: AcfForm) => {
  const {
    currentForm,
    loadingForm,
    loadingError,
    initNewForm,
    totalFormPages,
    submitSuccess,
    confirmations,
  } = useForm()
  const { modal, closeModal } = useUi()
  const { themeSettings } = useSettings()
  const { isTablet } = useViewport()

  useEffect(() => {
    if (id) {
      initNewForm(id, redirect, expires)
    }
  }, [id])

  if (loadingForm) {
    return (
      <div
        style={{
          paddingBlock: 24,
        }}>
        <RaHeading component="h4">{translate('LOADING_FORM')}</RaHeading>
        <RaFormSkeletonLoader />
      </div>
    )
  }

  if (loadingError) {
    return (
      <RaTypography>
        {translate('FORM_ERROR')}: {loadingError}
      </RaTypography>
    )
  }

  if (submitSuccess && typeof submitSuccess === 'string') {
    return <RaRichText content={confirmations?.message} />
  }

  if (!currentForm) {
    return <RaTypography>{translate('FORM_ERROR')}</RaTypography>
  }

  const isPaginatedForm = totalFormPages > 1

  return (
    <ErrorBoundary>
      <RaFormStyles>
        {modal.isTravelplan && (
          <div
            style={{
              marginBottom: 20,
              display: 'flex',
              justifyContent: 'space-between',
              alignContent: 'center',
            }}>
            <RaButton
              variant="text-primary"
              label={modal.customCloseButton}
              onClick={closeModal}
              startIcon={<RaIcon icon={ICONS.CHEVRON_LEFT} />}
            />
            {themeSettings?.text_phone && (
              <RaButton
                variant={isTablet ? 'icon' : 'text-primary'}
                label={isTablet ? '' : themeSettings.text_phone}
                icon={isTablet ? ICONS.MOBILE : null}
                link={{
                  title: themeSettings.text_phone,
                  url: `tel:${themeSettings.phone}`,
                }}
              />
            )}
          </div>
        )}
        {isPaginatedForm && <RaFormPagination />}
        {!isPaginatedForm && (
          <GravityFormsPage
            fields={currentForm.fields}
            isLastPage={true}
            submitButton={currentForm?.button}
          />
        )}
      </RaFormStyles>
    </ErrorBoundary>
  )
}
